/**
 * User management API schemas
 */

import { z } from 'zod';
import { Role, Status } from '@pillar-portal/database/dist/enums';
import { PositiveIntSchema } from './common.schemas';
import {
    PermissionGrantSchema,
    RoleAssignmentDetailedSchema,
    RoleAssignmentPermissionsViewSchema,
} from '../permission.types';
import {
    PhysicalTerminalAccessExpectationSchema,
    RoleAssignmentCustomConfigSchema,
} from '../backend_config.types';
import { UserEmailIdentitySchema } from '../email-identity';
import {
    countAcceptedLoginInvitationDeliveries,
    countUnconfirmedLoginInvitationDeliveries,
} from '../user.types';
import type {
    AssignRoleRequest,
    LoginInvitationAdministrationState,
    LoginInvitationDelivery,
    ProvisionedUser,
    ProvisionOrganizationalUserCreatedUser,
    ProvisionOrganizationalUserRequest,
    ProvisionOrganizationalUserResponse,
    ProvisionInternalUserCreatedUser,
    ProvisionInternalUserResponse,
    ProvisionUserRequest,
    RevokeRoleRequest,
    ResendAllLoginInvitationsPage,
    ResendAllLoginInvitationsRequest,
    RequestAddEmailVerificationRequest,
    RequestAddEmailVerificationResponse,
    RequestAddPhoneVerificationRequest,
    RequestAddPhoneVerificationResponse,
    SetUserMfaRequiredRequest,
    MfaResetRequest,
    MfaResetResponse,
    EmailAuthenticationAttemptLimitResetRequest,
    SetUserPhonesRequest,
    UserProfileDetailedPublicResponse,
    UserProfilePublicResponse,
    UpdateRoleAssignmentRequest,
    UpdateUserResponse,
    UpdateRoleAssignmentBodyRequest,
    VerifyAddPhoneRequest,
    LoginInvitationDeliveryV1,
    ProvisionedUserV1,
    LoginInvitationAdministrationStateV1,
    ResendAllLoginInvitationsPageV1,
    UserProfilePublicResponseV1,
    UserProfileDetailedPublicResponseV1,
} from '../user.types';

export const LoginInvitationDeliveryV2Schema: z.ZodType<LoginInvitationDelivery> =
    z.discriminatedUnion('delivery_outcome', [
        z.object({ delivery_outcome: z.literal('not_attempted') }).strict(),
        z
            .object({
                delivery_outcome: z.literal('provider_accepted'),
                attempted_at: z.string().datetime(),
            })
            .strict(),
        z
            .object({
                delivery_outcome: z.literal('failed'),
                attempted_at: z.string().datetime(),
            })
            .strict(),
        z
            .object({
                delivery_outcome: z.literal('unconfirmed'),
                attempted_at: z.string().datetime(),
            })
            .strict(),
    ]);

export const ProvisionedUserV2Schema: z.ZodType<ProvisionedUser> = z.discriminatedUnion(
    'activation_state',
    [
        z
            .object({
                activation_state: z.literal('pending_first_login'),
                user_uuid: z.string().uuid(),
                first_login_expires_at: z.string().datetime(),
                delivery: LoginInvitationDeliveryV2Schema,
            })
            .strict(),
        z
            .object({
                activation_state: z.literal('active_verification_bypassed'),
                user_uuid: z.string().uuid(),
                delivery: LoginInvitationDeliveryV2Schema,
            })
            .strict(),
    ]
);

export const LoginInvitationAdministrationStateV2Schema: z.ZodType<LoginInvitationAdministrationState> =
    z
        .object({
            user_uuid: z.string().uuid(),
            user_status: z.literal('PENDING'),
            self_service_revoked: z.boolean(),
            current_invitation_expires_at: z.string().datetime().nullable(),
            delivery: LoginInvitationDeliveryV2Schema,
        })
        .strict();

const LoginInvitationCursorSchema: z.ZodType<string> = z.string().min(1).max(2048);

export const ResendAllLoginInvitationsRequestSchema: z.ZodType<ResendAllLoginInvitationsRequest> = z
    .object({
        cursor: LoginInvitationCursorSchema.optional(),
        limit: z.number().int().positive().max(50).optional(),
    })
    .strict();

export const ResendAllLoginInvitationsPageV2Schema: z.ZodType<ResendAllLoginInvitationsPage> = z
    .object({
        results: z
            .array(
                z.discriminatedUnion('result_kind', [
                    z
                        .object({
                            result_kind: z.literal('resent'),
                            invitation: LoginInvitationAdministrationStateV2Schema,
                        })
                        .strict(),
                    z
                        .object({
                            result_kind: z.literal('failed'),
                            user_uuid: z.string().uuid(),
                        })
                        .strict(),
                ])
            )
            .max(50),
        resent_count: z.number().int().nonnegative(),
        failed_count: z.number().int().nonnegative(),
        unconfirmed_count: z.number().int().nonnegative(),
        next_cursor: LoginInvitationCursorSchema.optional(),
    })
    .strict()
    .superRefine((page, context) => {
        const resentCount: number = countAcceptedLoginInvitationDeliveries(page.results);
        const unconfirmedCount: number = countUnconfirmedLoginInvitationDeliveries(page.results);
        const failedCount: number = page.results.length - resentCount - unconfirmedCount;
        if (
            page.resent_count !== resentCount ||
            page.failed_count !== failedCount ||
            page.unconfirmed_count !== unconfirmedCount
        ) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Invitation resend page counts must match its results',
            });
        }
    });

/**
 * Provision user request (unified for both IUser and OUser)
 */
export const ProvisionUserRequestSchema: z.ZodType<ProvisionUserRequest> = z.object({
    primary_email: z.string().email(),
    emails: z.array(z.string().email()),
    role: z.enum(Role),
    organization_id: z.number().nullable(), // Required for organizational roles, null/undefined for internal users
    first_name: z.string().nullable(),
    last_name: z.string().nullable(),
    skip_verification: z.boolean().optional(),
    policies: z.array(z.string().uuid()).optional(),
});

/**
 * Provision user response
 */
export const ProvisionUserResponseSchema = z.object({
    user: z.lazy(() => UserProfileDetailedResponseSchema).nullable(),
    invitation_token: z.string().nullable(),
    invitation_expires_at: z.string().nullable(), // ISO date string
    primary_email: z.string().email(),
});

export const ProvisionInternalUserCreatedUserSchema: z.ZodType<ProvisionInternalUserCreatedUser> =
    z.object({
        user_uuid: z.string().uuid(),
    });

export const ProvisionInternalUserResponseSchema: z.ZodType<ProvisionInternalUserResponse> =
    z.object({
        user: ProvisionInternalUserCreatedUserSchema.nullable(),
        invitation_token: z.string().nullable(),
        invitation_expires_at: z.string().nullable(), // ISO date string
        primary_email: z.string().email(),
    });

export const ProvisionOrganizationalUserRequestSchema: z.ZodType<ProvisionOrganizationalUserRequest> =
    z.object({
        primary_email: z.string().email(),
        emails: z.array(z.string().email()),
        role: z.enum(Role),
        organization_uuid: z.string().uuid(),
        first_name: z.string().nullable(),
        last_name: z.string().nullable(),
        skip_verification: z.boolean().optional(),
        policies: z.array(z.string().uuid()).optional(),
    });

export const ProvisionOrganizationalUserCreatedUserSchema: z.ZodType<ProvisionOrganizationalUserCreatedUser> =
    z.object({
        user_uuid: z.string().uuid(),
    });

export const ProvisionOrganizationalUserResponseSchema: z.ZodType<ProvisionOrganizationalUserResponse> =
    z.object({
        user: ProvisionOrganizationalUserCreatedUserSchema.nullable(),
        invitation_token: z.string().nullable(),
        invitation_expires_at: z.string().nullable(), // ISO date string
        primary_email: z.string().email(),
    });

/**
 * Update user request
 */
export const UpdateUserRequestSchema = z.object({
    primary_email: z.string().email().optional(),
    status: z.enum(Status).optional(),
    first_name: z.string().nullable().optional(),
    last_name: z.string().nullable().optional(),
});

export const UpdateUserResponseSchema: z.ZodType<UpdateUserResponse> = z.object({
    user_uuid: z.string().uuid(),
    first_name: z.string().nullable(),
    last_name: z.string().nullable(),
    primary_email: z.string(),
    emails: z.array(z.string()),
    phones: z.array(z.string()).optional(),
    primary_phone: z.string().nullable().optional(),
    totp_enabled: z.boolean(),
    status: z.enum(Status),
    invitation_token: z.string().optional(),
});

/**
 * User profile response (basic, without role assignments)
 */
export const UserProfileResponseSchema = z.object({
    user_id: z.number(),
    user_uuid: z.string().uuid().optional(),
    first_name: z.string().nullable(),
    last_name: z.string().nullable(),
    primary_email: z.string(),
    emails: z.array(z.string()),
    phones: z.array(z.string()).default([]),
    primary_phone: z.string().nullable().default(null),
    totp_enabled: z.boolean(),
    status: z.enum(Status),
    invitation_token: z.string().optional(),
});

/**
 * User profile response with full details (includes role assignments)
 */
export const UserProfileDetailedResponseSchema = UserProfileResponseSchema.extend({
    default_role_assignment: RoleAssignmentDetailedSchema.nullable(),
    other_role_assignments: z.array(RoleAssignmentDetailedSchema),
});

export const UserProfilePublicResponseV2Schema = z
    .object({
        user_uuid: z.string().uuid(),
        first_name: z.string().nullable(),
        last_name: z.string().nullable(),
        primary_email: z.string(),
        emails: z.array(z.string()),
        unverified_emails: z.array(z.string()),
        pending_primary_email: z.string().nullable(),
        phones: z.array(z.string()).optional(),
        unverified_phones: z.array(z.string()),
        primary_phone: z.string().nullable().optional(),
        pending_primary_phone: z.string().nullable(),
        totp_enabled: z.boolean(),
        mfa_required: z.boolean(),
        mfa_email_allowed: z.boolean(),
        mfa_sms_allowed: z.boolean(),
        status: z.enum(Status),
        login_invitation: LoginInvitationAdministrationStateV2Schema.nullable(),
    })
    .strict() satisfies z.ZodType<UserProfilePublicResponse>;

export const UserProfileDetailedPublicResponseV2Schema: z.ZodType<UserProfileDetailedPublicResponse> =
    UserProfilePublicResponseV2Schema.extend({
        default_role_assignment: RoleAssignmentPermissionsViewSchema.nullable(),
        other_role_assignments: z.array(RoleAssignmentPermissionsViewSchema),
    });

/**
 * Assign role request
 */
export const AssignRoleRequestSchemaBase: z.ZodType<AssignRoleRequest> = z.object({
    user_uuids: z.array(z.string().uuid()).min(1),
    role: z.enum(Role),
    organization_uuid: z.string().uuid().nullable(), // Required for organizational roles, null for global roles
    permissions: z.array(PermissionGrantSchema).optional(), // Optional permissions to grant immediately
});

export const AssignRoleRequestSchema: z.ZodType<AssignRoleRequest> = AssignRoleRequestSchemaBase;

/**
 * Revoke role request
 */
export const RevokeRoleRequestSchema: z.ZodType<RevokeRoleRequest> = z.object({
    role_assignment_uuid: z.string().uuid(),
    user_uuid: z.string().uuid(), // Cross-check for safety
});

/**
 * Update role assignment request
 */
export const UpdateRoleAssignmentBodyRequestSchema: z.ZodType<UpdateRoleAssignmentBodyRequest> =
    z.object({
        user_uuid: z.string().uuid(), // Cross-check for safety
        role: z.enum(Role).optional(),
        profile_name: z.string().optional(), // Deprecated - not supported in current schema
        user_config: RoleAssignmentCustomConfigSchema.optional(),
        physical_terminal_access_expectation: PhysicalTerminalAccessExpectationSchema.optional(),
    });

export const UpdateRoleAssignmentRequestSchema: z.ZodType<UpdateRoleAssignmentRequest> = z.object({
    role_assignment_uuid: z.string().uuid(),
    user_uuid: z.string().uuid(), // Cross-check for safety
    role: z.enum(Role).optional(),
    profile_name: z.string().optional(), // Deprecated - not supported in current schema
    user_config: RoleAssignmentCustomConfigSchema.optional(),
    physical_terminal_access_expectation: PhysicalTerminalAccessExpectationSchema.optional(),
});

/**
 * Delete my account request (self-delete)
 */
export const DeleteMyAccountRequestSchema = z.object({
    password: z.string().min(1),
});

/**
 * Delete my account response
 */
export const DeleteMyAccountResponseSchema = z.object({
    success: z.boolean(),
    message: z.string(),
});

/**
 * Get profile response (simplified user profile)
 */
export const GetProfileResponseSchema = z.object({
    user: z
        .object({
            user_uuid: z.string().uuid(),
            primary_email: z.string(),
            emails: z.array(z.string()),
            status: z.enum(Status),
            totp_enabled: z.boolean(),
        })
        .nullable(),
});

/**
 * Update profile request
 */
export const UpdateProfileRequestSchema = z
    .object({
        primary_email: z.string().email().optional(),
        first_name: z.string().nullable().optional(),
        last_name: z.string().nullable().optional(),
    })
    .refine(
        (request) =>
            request.primary_email !== undefined ||
            request.first_name !== undefined ||
            request.last_name !== undefined,
        { message: 'At least one profile field must be provided' }
    );

/**
 * Reset password admin request
 */
export const ResetPasswordAdminRequestSchema = z.object({
    new_password: z.string().min(1),
});

export const MfaResetRequestSchema: z.ZodType<MfaResetRequest> = z
    .object({
        reason: z.string().max(2000),
        identity_verification_attested: z.boolean(),
    })
    .strict();

export const MfaResetResponseSchema: z.ZodType<MfaResetResponse> = z
    .object({
        reset_outcome: z.literal('succeeded'),
        notification_outcome: z.enum(['sent', 'failed', 'unavailable']),
    })
    .strict();

export const EmailAuthenticationAttemptLimitResetRequestSchema: z.ZodType<EmailAuthenticationAttemptLimitResetRequest> =
    z
        .object({
            target_user_uuid: z.string().uuid(),
        })
        .strict();

/**
 * Request password reset admin request
 */
export const RequestPasswordResetAdminRequestSchema: z.ZodObject<{
    email: z.ZodString;
}> = z.object({
    email: z.string().email(),
});

/**
 * Set user MFA requirement request
 */
export const SetUserMfaRequiredRequestSchema: z.ZodType<SetUserMfaRequiredRequest> = z.object({
    required: z.boolean(),
});

/**
 * Set user emails request
 */
export const SetUserEmailsRequestSchema = z
    .object({
        emails: z.array(z.string().email()).min(1),
        primary_email: z.string().email(),
    })
    .superRefine((value, context) => {
        const uniqueEmails = new Set<string>(value.emails);
        if (uniqueEmails.size !== value.emails.length) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'emails must be unique',
                path: ['emails'],
            });
        }
        if (!uniqueEmails.has(value.primary_email)) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'primary_email must be included in emails',
                path: ['primary_email'],
            });
        }
    });

export const UserPhoneNumberInputSchema: z.ZodType<string> = z.string().trim().min(1);

/**
 * Set user phone numbers request
 */
export const SetUserPhonesRequestSchema: z.ZodType<SetUserPhonesRequest> = z
    .object({
        phones: z.array(UserPhoneNumberInputSchema),
        primary_phone: UserPhoneNumberInputSchema.nullable(),
    })
    .superRefine((value, context) => {
        const uniquePhones = new Set<string>(value.phones);
        if (uniquePhones.size !== value.phones.length) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'phones must be unique',
                path: ['phones'],
            });
        }
        if (value.primary_phone !== null && !uniquePhones.has(value.primary_phone)) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'primary_phone must be included in phones',
                path: ['primary_phone'],
            });
        }
        if (value.primary_phone === null && value.phones.length > 0) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'primary_phone is required when phones are present',
                path: ['primary_phone'],
            });
        }
    });

export const RequestAddEmailVerificationRequestSchema: z.ZodType<RequestAddEmailVerificationRequest> =
    z.object({
        email: UserEmailIdentitySchema,
        skip_verification: z.boolean().optional(),
    });

export const RequestAddEmailVerificationResponseSchema: z.ZodType<RequestAddEmailVerificationResponse> =
    z.object({
        message: z.string(),
        verification_link: z.string().optional(),
    });

export const RequestAddPhoneVerificationRequestSchema: z.ZodType<RequestAddPhoneVerificationRequest> =
    z.object({
        phone: UserPhoneNumberInputSchema,
        skip_verification: z.boolean().optional(),
    });

export const RequestAddPhoneVerificationResponseSchema: z.ZodType<RequestAddPhoneVerificationResponse> =
    z.object({
        message: z.string(),
        delivery_destination: z.string().optional(),
        expires_at: z.string().optional(),
    });

export const VerifyAddPhoneRequestSchema: z.ZodType<VerifyAddPhoneRequest> = z.object({
    phone: UserPhoneNumberInputSchema,
    code: z.string().trim().min(1),
});

/**
 * Get emails response
 */
export const GetEmailsResponseSchema = z.object({
    primary_email: z.string(),
    emails: z.array(
        z.object({
            email: z.string(),
            is_primary: z.boolean(),
            is_verified: z.boolean(),
        })
    ),
});

/**
 * Get my roles response
 */
export const GetMyRolesResponseSchema = z.object({
    roles: z.array(
        z.object({
            role: z.enum(Role),
            organization_uuid: z.string().uuid().nullable(),
            organization_name: z.string().optional(),
        })
    ),
});

/**
 * Common param schemas
 */
export const UserIdParamSchema: z.ZodType<number> = PositiveIntSchema;
export const UserUuidParamSchema: z.ZodType<string> = z.string().uuid();
export const RoleAssignmentIdParamSchema: z.ZodType<number> = PositiveIntSchema;

/** Immutable physical V1, captured before the approved delivery-uncertainty extension. */
export const LoginInvitationDeliveryV1Schema: z.ZodType<LoginInvitationDeliveryV1> =
    z.discriminatedUnion('delivery_outcome', [
        z.object({ delivery_outcome: z.literal('not_attempted') }).strict(),
        z
            .object({
                delivery_outcome: z.literal('provider_accepted'),
                attempted_at: z.string().datetime(),
            })
            .strict(),
        z
            .object({
                delivery_outcome: z.literal('failed'),
                attempted_at: z.string().datetime(),
            })
            .strict(),
    ]);

export const ProvisionedUserV1Schema: z.ZodType<ProvisionedUserV1> = z.discriminatedUnion(
    'activation_state',
    [
        z
            .object({
                activation_state: z.literal('pending_first_login'),
                user_uuid: z.string().uuid(),
                first_login_expires_at: z.string().datetime(),
                delivery: LoginInvitationDeliveryV1Schema,
            })
            .strict(),
        z
            .object({
                activation_state: z.literal('active_verification_bypassed'),
                user_uuid: z.string().uuid(),
                delivery: LoginInvitationDeliveryV1Schema,
            })
            .strict(),
    ]
);

export const LoginInvitationAdministrationStateV1Schema: z.ZodType<LoginInvitationAdministrationStateV1> =
    z
        .object({
            user_uuid: z.string().uuid(),
            user_status: z.literal('PENDING'),
            self_service_revoked: z.boolean(),
            current_invitation_expires_at: z.string().datetime().nullable(),
            delivery: LoginInvitationDeliveryV1Schema,
        })
        .strict();

export const ResendAllLoginInvitationsPageV1Schema: z.ZodType<ResendAllLoginInvitationsPageV1> = z
    .object({
        results: z
            .array(
                z.discriminatedUnion('result_kind', [
                    z
                        .object({
                            result_kind: z.literal('resent'),
                            invitation: LoginInvitationAdministrationStateV1Schema,
                        })
                        .strict(),
                    z
                        .object({
                            result_kind: z.literal('failed'),
                            user_uuid: z.string().uuid(),
                        })
                        .strict(),
                ])
            )
            .max(50),
        resent_count: z.number().int().nonnegative(),
        failed_count: z.number().int().nonnegative(),
        next_cursor: LoginInvitationCursorSchema.optional(),
    })
    .strict()
    .superRefine((page, context) => {
        const resentCount: number = countAcceptedLoginInvitationDeliveries(page.results);
        const failedCount: number = page.results.length - resentCount;
        if (page.resent_count !== resentCount || page.failed_count !== failedCount) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Invitation resend page counts must match its results',
            });
        }
    });

export const UserProfilePublicResponseV1Schema = z
    .object({
        user_uuid: z.string().uuid(),
        first_name: z.string().nullable(),
        last_name: z.string().nullable(),
        primary_email: z.string(),
        emails: z.array(z.string()),
        unverified_emails: z.array(z.string()),
        pending_primary_email: z.string().nullable(),
        phones: z.array(z.string()).optional(),
        unverified_phones: z.array(z.string()),
        primary_phone: z.string().nullable().optional(),
        pending_primary_phone: z.string().nullable(),
        totp_enabled: z.boolean(),
        mfa_required: z.boolean(),
        mfa_email_allowed: z.boolean(),
        mfa_sms_allowed: z.boolean(),
        status: z.enum(Status),
        login_invitation: LoginInvitationAdministrationStateV1Schema.nullable(),
    })
    .strict() satisfies z.ZodType<UserProfilePublicResponseV1>;

export const UserProfileDetailedPublicResponseV1Schema: z.ZodType<UserProfileDetailedPublicResponseV1> =
    UserProfilePublicResponseV1Schema.extend({
        default_role_assignment: RoleAssignmentPermissionsViewSchema.nullable(),
        other_role_assignments: z.array(RoleAssignmentPermissionsViewSchema),
    });

/** A legacy response must never disguise a possibly completed delivery. */
export class InvitationResponseV1IncompatibilityError extends Error {
    constructor() {
        super(
            'Delivery unconfirmed. This API version cannot represent the outcome. The user and invitation may already exist; do not retry automatically. Use an updated client before explicitly resending.'
        );
    }
}

function downgradeLoginInvitationDeliveryV1(
    loginInvitationDelivery: LoginInvitationDelivery
): LoginInvitationDeliveryV1 {
    switch (loginInvitationDelivery.delivery_outcome) {
        case 'unconfirmed':
            throw new InvitationResponseV1IncompatibilityError();
        case 'not_attempted':
        case 'provider_accepted':
        case 'failed':
            return LoginInvitationDeliveryV1Schema.parse(loginInvitationDelivery);
    }
}

export const ProvisionedUserResponseContract = {
    variants: {
        v1: {
            normalize: (input: unknown): ProvisionedUser => ProvisionedUserV1Schema.parse(input),
        },
        v2: {
            normalize: (input: unknown): ProvisionedUser => ProvisionedUserV2Schema.parse(input),
        },
    },
    writerVariant: 'v2',
} as const;

export const ProvisionedUserResponseDowngrades = {
    v1: (provisionedUser: ProvisionedUser): ProvisionedUserV1 => {
        const validatedProvisionedUser = ProvisionedUserV2Schema.parse(provisionedUser);
        return ProvisionedUserV1Schema.parse({
            ...validatedProvisionedUser,
            delivery: downgradeLoginInvitationDeliveryV1(validatedProvisionedUser.delivery),
        });
    },
};

export const LoginInvitationAdministrationStateResponseContract = {
    variants: {
        v1: {
            normalize: (input: unknown): LoginInvitationAdministrationState =>
                LoginInvitationAdministrationStateV1Schema.parse(input),
        },
        v2: {
            normalize: (input: unknown): LoginInvitationAdministrationState =>
                LoginInvitationAdministrationStateV2Schema.parse(input),
        },
    },
    writerVariant: 'v2',
} as const;

export const LoginInvitationAdministrationStateResponseDowngrades = {
    v1: (
        loginInvitationAdministrationState: LoginInvitationAdministrationState
    ): LoginInvitationAdministrationStateV1 => {
        const validatedLoginInvitationAdministrationState =
            LoginInvitationAdministrationStateV2Schema.parse(loginInvitationAdministrationState);
        return LoginInvitationAdministrationStateV1Schema.parse({
            ...validatedLoginInvitationAdministrationState,
            delivery: downgradeLoginInvitationDeliveryV1(
                validatedLoginInvitationAdministrationState.delivery
            ),
        });
    },
};

export const ResendAllLoginInvitationsPageResponseContract = {
    variants: {
        v1: {
            normalize: (input: unknown): ResendAllLoginInvitationsPage => ({
                ...ResendAllLoginInvitationsPageV1Schema.parse(input),
                unconfirmed_count: 0,
            }),
        },
        v2: {
            normalize: (input: unknown): ResendAllLoginInvitationsPage =>
                ResendAllLoginInvitationsPageV2Schema.parse(input),
        },
    },
    writerVariant: 'v2',
} as const;

export const ResendAllLoginInvitationsPageResponseDowngrades = {
    v1: (
        resendAllLoginInvitationsPage: ResendAllLoginInvitationsPage
    ): ResendAllLoginInvitationsPageV1 => {
        const validatedResendAllLoginInvitationsPage = ResendAllLoginInvitationsPageV2Schema.parse(
            resendAllLoginInvitationsPage
        );
        if (validatedResendAllLoginInvitationsPage.unconfirmed_count !== 0)
            throw new InvitationResponseV1IncompatibilityError();
        return ResendAllLoginInvitationsPageV1Schema.parse({
            results: validatedResendAllLoginInvitationsPage.results.map((result) =>
                result.result_kind === 'failed'
                    ? result
                    : {
                          ...result,
                          invitation: LoginInvitationAdministrationStateResponseDowngrades.v1(
                              result.invitation
                          ),
                      }
            ),
            resent_count: validatedResendAllLoginInvitationsPage.resent_count,
            failed_count: validatedResendAllLoginInvitationsPage.failed_count,
            ...(validatedResendAllLoginInvitationsPage.next_cursor === undefined
                ? {}
                : { next_cursor: validatedResendAllLoginInvitationsPage.next_cursor }),
        });
    },
};

export const UserProfileDetailedPublicResponseContract = {
    variants: {
        v1: {
            normalize: (input: unknown): UserProfileDetailedPublicResponse =>
                UserProfileDetailedPublicResponseV1Schema.parse(input),
        },
        v2: {
            normalize: (input: unknown): UserProfileDetailedPublicResponse =>
                UserProfileDetailedPublicResponseV2Schema.parse(input),
        },
    },
    writerVariant: 'v2',
} as const;

export const UserProfileDetailedPublicResponseDowngrades = {
    v1: (
        userProfileDetailedPublicResponse: UserProfileDetailedPublicResponse
    ): UserProfileDetailedPublicResponseV1 => {
        const validatedUserProfileDetailedPublicResponse =
            UserProfileDetailedPublicResponseV2Schema.parse(userProfileDetailedPublicResponse);
        return UserProfileDetailedPublicResponseV1Schema.parse({
            ...validatedUserProfileDetailedPublicResponse,
            login_invitation:
                validatedUserProfileDetailedPublicResponse.login_invitation === null
                    ? null
                    : LoginInvitationAdministrationStateResponseDowngrades.v1(
                          validatedUserProfileDetailedPublicResponse.login_invitation
                      ),
        });
    },
};
